import * as d3 from 'd3';
import moment from 'moment';
import { getMessage } from '@tcc/i18n';
import { createUnit } from '../../i18n/unitQtyWrapper';
import { gridlines } from '../components/PastWeather/css/BaseChart.css';

function localizeD3() {
  return d3.timeFormatDefaultLocale({
    decimal: ',',
    thousands: '.',
    grouping: [3],
    currency: ['$', ''],
    dateTime: '%a %b %e %X %Y',
    date: '%m.%d.%Y',
    time: '%H:%M:%S',
    periods: ['AM', 'PM'],
    days: [
      getMessage('weekdays.Monday'),
      getMessage('weekdays.Tuesday'),
      getMessage('weekdays.Wednesday'),
      getMessage('weekdays.Thursday'),
      getMessage('weekdays.Friday'),
      getMessage('weekdays.Saturday'),
      getMessage('weekdays.Sunday'),
    ],
    shortDays: [
      getMessage('weekdays.Mon'),
      getMessage('weekdays.Tue'),
      getMessage('weekdays.Wed'),
      getMessage('weekdays.Thu'),
      getMessage('weekdays.Fri'),
      getMessage('weekdays.Sat'),
      getMessage('weekdays.Sun'),
    ],
    months: [
      getMessage('monthNames.January'),
      getMessage('monthNames.February'),
      getMessage('monthNames.March'),
      getMessage('monthNames.April'),
      getMessage('monthNames.May'),
      getMessage('monthNames.June'),
      getMessage('monthNames.July'),
      getMessage('monthNames.August'),
      getMessage('monthNames.September'),
      getMessage('monthNames.October'),
      getMessage('monthNames.November'),
      getMessage('monthNames.December'),
    ],
    shortMonths: [
      getMessage('monthNames.Jan'),
      getMessage('monthNames.Feb'),
      getMessage('monthNames.Mar'),
      getMessage('monthNames.Apr'),
      getMessage('monthNames.May'),
      getMessage('monthNames.Jun'),
      getMessage('monthNames.Jul'),
      getMessage('monthNames.Aug'),
      getMessage('monthNames.Sep'),
      getMessage('monthNames.Oct'),
      getMessage('monthNames.Nov'),
      getMessage('monthNames.Dec'),
    ],
  });
}

function createAxes({
  node,
  height,
  xScale,
  yScale,
  timeDiff,
  padding,
  ticks,
  width,
}) {
  localizeD3();
  const xAxis = d3
    .axisBottom(xScale)
    .ticks(Math.min(8, timeDiff))
    .tickFormat(d3.timeFormat('%b %e'))
    .tickSizeOuter(0);

  // create x axis
  d3.select(node)
    .append('g')
    .attr('transform', `translate(-19, ${height})`)
    .call(xAxis);

  const yAxis = d3.axisLeft(yScale).tickValues(ticks).tickSizeOuter(0);

  // create y axis
  d3.select(node)
    .append('g')
    .attr('transform', `translate(${padding / 2}, 0)`)
    .call(yAxis);

  const yAxisGrid = d3
    .axisLeft(yScale)
    .tickValues(ticks)
    .tickSize(-width - padding)
    .tickFormat('')
    .tickSizeOuter(0);

  // create grid lines
  d3.select(node)
    .append('g')
    .attr('class', gridlines)
    .attr('transform', `translate(${padding / 2}, 0)`)
    .call(yAxisGrid);
}

export const getWidthForLength = (length, containerWidth) => {
  const width = !containerWidth ? 500 : containerWidth - 500;
  return Math.ceil(width / length) * length;
};

const getSizesForLength = (length, containerWidth) => {
  const height = 250;
  const width = getWidthForLength(length, containerWidth);
  const padding = 70;
  return { height, width, padding };
};

const getMinAndMax = (minScalar, maxScalar) => {
  const orderOfMagnitude = String(maxScalar - minScalar).split('.')[0].length;
  const factor =
    orderOfMagnitude === 1 ? orderOfMagnitude : 10 ** orderOfMagnitude;
  const tick = 0.25 * factor;
  const yMax = Math.ceil(maxScalar / tick + 2) * tick;

  let yMin;
  if (minScalar < 0) {
    yMin = Math.min(minScalar, -tick);
  } else {
    yMin = Math.min(minScalar, 0);
  }
  return { yMax, yMin, tick };
};

const getDatesAndTimeDiff = (startDate, endDate) => {
  const beginDate = startDate ? moment(startDate) : moment().subtract(365, 'd');
  const finalDate = endDate ? moment(endDate) : moment();
  const timeDiff = finalDate.diff(beginDate, 'days', false) + 1;
  return { beginDate, finalDate, timeDiff };
};

export const getProjections = (width, timeDiff) => {
  const portionOfScale = Math.round(width / timeDiff);
  const projectPosition = (position) => {
    const value = position * portionOfScale;
    return value;
  };
  const projectIndex = (value) => Math.round(value / portionOfScale);
  return { projectPosition, projectIndex };
};

export function generateChartConfig(
  node,
  maxScalar,
  minScalar,
  length,
  startDate,
  endDate,
  containerWidth
) {
  const { height, width, padding } = getSizesForLength(length, containerWidth);
  const { yMin, yMax, tick } = getMinAndMax(minScalar, maxScalar);
  const { beginDate, finalDate, timeDiff } = getDatesAndTimeDiff(
    startDate,
    endDate
  );
  const factor = 1.1;

  const yScale = d3
    .scaleLinear()
    .domain(d3.extent([maxScalar * factor, minScalar * factor]))
    .range([height, 0]);
  const xScale = d3
    .scaleTime()
    .domain([beginDate.toDate(), finalDate.toDate()])
    .range([padding, width + padding]);
  const ticks = yScale.ticks();
  yScale.nice(ticks.length);

  createAxes({ node, height, xScale, yScale, timeDiff, padding, ticks, width });

  return {
    height,
    width,
    padding,
    xScale,
    yScale,
    yMax,
    yMin,
    tick,
    timeDiff,
  };
}

export function accumulateData(data) {
  return data.reduce(
    (prev, curr, index) => prev.concat(prev[index] + curr),
    [0]
  );
}

export const formatGraphEntries = (quantity, unit, localeGroup) =>
  createUnit(quantity, unit).forLocaleGroup(localeGroup);

export const formatGraphValues = (quantity, unit, localeGroup) => {
  const unitQtyObject = formatGraphEntries(quantity, unit, localeGroup);

  if (Number.isNaN(Number(unitQtyObject.scalar))) {
    return null;
  }
  return unitQtyObject.scalar;
};

export const formatGraphUnit = (quantity, unit, localeGroup) =>
  formatGraphEntries(quantity, unit, localeGroup).unitKey;

export const buildArea = (xScale, yScale, y0) =>
  d3
    .area()
    .x((_, i) => xScale(i))
    .y0(y0)
    .y1((d) => yScale(d));

export const buildLine = (xScale, yScale) =>
  d3
    .line()
    .x((_, i) => xScale(i))
    .y((d) => yScale(d));

const drawPath = (data, className, pathToDraw) => {
  const path = d3
    .create('svg:path')
    .datum(data)
    .attr('class', className)
    .attr('d', pathToDraw);

  return path.node();
};

export const drawLine = (data, className, linePathToDraw) =>
  drawPath(data, className, linePathToDraw);

export const drawArea = (data, className, areaPathToDraw) =>
  drawPath(data, className, areaPathToDraw);

export const drawFocusDot = (className) => {
  const dot = d3
    .create('svg:g')
    .attr('class', className)
    .style('display', 'none');

  dot
    .append('rect')
    .attr('height', 7)
    .attr('width', 7)
    .attr('transsform', 'translate(10, 10)');

  return dot.node();
};

export const buildLocaleObject = (data, localeGroup) => {
  const unit = data.u;
  const quantities = data.q.map((quantity) =>
    formatGraphValues(quantity, unit, localeGroup)
  );
  const normalizedUnit = formatGraphUnit(data.q[0] || 0, unit, localeGroup);

  return {
    q: quantities,
    u: normalizedUnit,
  };
};
